import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { EmployeesTable } from "@/components/employees/employees-table";
import { EmployeesTransfer } from "@/components/employees/employees-transfer";
import { Can } from "@/components/workspace/can";
import { Link } from "@/core/lib/navigation";
import { useLabels } from "@/hooks/use-labels";
import { activeEmployeeImportQuery } from "@/lib/loaders";
import { DEFAULT_EMPLOYEE_LIST_PARAMS, employeesPageQuery, warm } from "@/lib/workspace-queries";
import { Button } from "@/ui/components/ui/button";

export const Route = createFileRoute("/_workspace/employees/")({
  // `?import=1` opens the CSV import straight away — the dashboard's setup
  // checklist links here for "Import CSV".
  validateSearch: (search: Record<string, unknown>) => ({
    import: search.import === 1 || search.import === "1" || search.import === true || undefined,
  }),
  loader: ({ context }) => {
    // The table's first page is a head start only — it renders its own skeleton.
    warm(context.queryClient.ensureQueryData(employeesPageQuery(DEFAULT_EMPLOYEE_LIST_PARAMS)));
    return context.queryClient.ensureQueryData(activeEmployeeImportQuery);
  },
  component: EmployeesPage,
});

function EmployeesPage() {
  const labels = useLabels();
  // Lets the import progress indicator render on first paint after a refresh
  // instead of waiting on a client round-trip.
  const { data: activeImport } = useQuery(activeEmployeeImportQuery);
  const { import: openImport } = Route.useSearch();

  return (
    <main className="flex flex-1 flex-col p-4 md:p-6">
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="space-y-1">
            <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground">
              Employees
            </p>
            <h1 className="text-2xl font-bold tracking-tight">{labels.people.plural}</h1>
            <p className="text-sm text-muted-foreground">
              Manage your {labels.people.lowerPlural}. Each {labels.people.lowerSingular} starts
              with a required core profile.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Can area="employees" level="edit">
              <EmployeesTransfer initialImport={activeImport ?? null} openImport={openImport} />
            </Can>
            <Can area="employees" level="edit">
              <Button asChild className="gap-1.5">
                <Link href="/employees/new">
                  <PlusIcon className="size-4" />
                  Add {labels.people.singular}
                </Link>
              </Button>
            </Can>
          </div>
        </div>

        <EmployeesTable />
      </div>
    </main>
  );
}
